import { STAGE_BADGE, getStageBadgeClass } from "../../utils/leadStageBadge";
import { useState, useEffect, useMemo, useCallback } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { cn } from "../../utils/tailwindUtils";
import { RefreshCw, Search, ChevronDown, ChevronLeft, ChevronRight, CheckCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { CUSTOMERSTATUSREPORTS, USERS_SIMPLE_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import ResearchMark from "../../components/customers/ResearchMark";
import { useNavigate } from "react-router-dom";
import { usePersistedState } from "../../hooks/usePersistedState";
import StateFilterSelect from "../../components/common/StateFilterSelect";
import dayjs from "dayjs";
import TruncatedText from "../../components/ui/truncated-text";

// ── Date presets ──────────────────────────────────────────────────────────────

const DATE_PRESETS = [
    { value: "all",        label: "All Time" },
    { value: "today",      label: "Today" },
    { value: "yesterday",  label: "Yesterday" },
    { value: "last_week",  label: "Last 7 Days" },
    { value: "last_month", label: "Last 30 Days" },
    { value: "this_month", label: "This Month" },
    { value: "custom",     label: "Custom Range" },
];

const resolveDateRange = (preset, customFrom, customTo) => {
    const today = dayjs();
    const fmt   = d => d.format("YYYY-MM-DD");
    switch (preset) {
        case "today":      return { from_date: fmt(today),                          to_date: fmt(today) };
        case "yesterday":  return { from_date: fmt(today.subtract(1, "day")),       to_date: fmt(today.subtract(1, "day")) };
        case "last_week":  return { from_date: fmt(today.subtract(7,  "day")),      to_date: fmt(today) };
        case "last_month": return { from_date: fmt(today.subtract(30, "day")),      to_date: fmt(today) };
        case "this_month": return { from_date: fmt(today.startOf("month")), to_date: fmt(today) };
        case "custom":     return (customFrom && customTo) ? { from_date: customFrom, to_date: customTo } : {};
        default:           return {};
    }
};

const FUNNEL_ORDER = [
    "New Lead", "Contacted", "Qualified", "Demo Fixed", "Demo Done",
    "Proposal", "Negotiation", "Closed Won", "Closed Lost", "Dis-Qualified",
    "On Hold", "Unanswered", "No Show", "Nurture Lead",
];

// ── main component ─────────────────────────────────────────────────────────────

const TimelineReports = () => {
    const { fetch } = useApiMethods();
    const navigate  = useNavigate();

    const [allData,     setAllData]     = useState([]);
    const [loading,     setLoading]     = useState(false);
    const [search,      setSearch]      = useState("");
    const [stageFilter, setStageFilter] = usePersistedState("timeline_report_stage", null);
    const [datePreset,  setDatePreset]  = usePersistedState("timeline_report_preset", "today");
    const [customFrom,  setCustomFrom]  = usePersistedState("timeline_report_from", "");
    const [customTo,    setCustomTo]    = usePersistedState("timeline_report_to",   "");
    const [users,          setUsers]          = useState([]);
    const [selectedUserId, setSelectedUserId] = usePersistedState("timeline_report_user", "");
    const [stateFilter, setStateFilter] = usePersistedState("timeline_report_state", "");
    const [stateCounts, setStateCounts] = useState(null);
    const [page,           setPage]           = useState(1);
    const [pageSize,    setPageSize]    = useState(25);

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const params = new URLSearchParams();
        if (from_date)      params.append("from_date", from_date);
        if (to_date)        params.append("to_date",   to_date);
        if (selectedUserId) params.append("user_id",   selectedUserId);
        if (stateFilter) params.append("state", stateFilter);
        const qs  = params.toString();
        const url = qs ? `${CUSTOMERSTATUSREPORTS}?${qs}` : CUSTOMERSTATUSREPORTS;
        fetch(url)
            .then(res => {
                const rows = Array.isArray(res?.data) ? res.data : (res?.data?.results || []);
                setAllData(rows);
                // Counts follow the date and rep picked; null keeps the overall list.
                setStateCounts(Array.isArray(res?.state_counts) ? res.state_counts : null);
                if (res?.status !== "success" && !rows.length) {
                    ToastMsg("error", res?.message || "Failed to load timeline");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load timeline"))
            .finally(() => setLoading(false));
    }, [datePreset, customFrom, customTo, selectedUserId, stateFilter]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        fetch(USERS_SIMPLE_URL).then(res => {
            if (res?.status === "success") setUsers(res.data || []);
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Unique to_status values ordered by funnel
    const stages = useMemo(() => {
        const present = new Set(allData.map(r => r.to_status).filter(Boolean));
        const ordered = FUNNEL_ORDER.filter(s => present.has(s));
        present.forEach(s => { if (!ordered.includes(s)) ordered.push(s); });
        return ordered;
    }, [allData]);

    const stageCounts = useMemo(() => {
        const counts = {};
        allData.forEach(r => {
            if (r.to_status) counts[r.to_status] = (counts[r.to_status] || 0) + 1;
        });
        return counts;
    }, [allData]);

    const filtered = useMemo(() => {
        let rows = allData;
        if (stageFilter) rows = rows.filter(r => r.to_status === stageFilter);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            rows = rows.filter(r =>
                r.customer_name?.toLowerCase().includes(q) ||
                r.contact_number?.toLowerCase().includes(q) ||
                r.user_name?.toLowerCase().includes(q)
            );
        }
        return rows;
    }, [allData, stageFilter, search]);

    // Reset to page 1 whenever filters change
    useEffect(() => { setPage(1); }, [stageFilter, search, pageSize]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);

    return (
        <ContainerCard>
            {/* ── fixed top: header + filters ── */}
            <div className="flex-shrink-0">
                {/* header */}
                <div className="flex items-center justify-between mb-4 px-1">
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Timeline Report</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Lead stage transition history — who moved what and when
                        </p>
                    </div>
                    <Button
                        onClick={load}
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-1 text-xs"
                        disabled={loading}
                    >
                        <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
                        Refresh
                    </Button>
                </div>

                {/* date filter */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div className="relative">
                        <select
                            value={datePreset}
                            onChange={e => {
                                setDatePreset(e.target.value);
                                if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); }
                            }}
                            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                        >
                            {DATE_PRESETS.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>

                    {datePreset === "custom" && (
                        <>
                            <input
                                type="date"
                                value={customFrom}
                                onChange={e => setCustomFrom(e.target.value)}
                                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400"
                            />
                            <span className="text-xs text-gray-400">to</span>
                            <input
                                type="date"
                                value={customTo}
                                onChange={e => setCustomTo(e.target.value)}
                                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400"
                            />
                            <Button onClick={() => load()} size="sm" variant="outline" className="text-xs px-3" disabled={loading}>Apply</Button>
                        </>
                    )}

                    <div className="relative">
                        <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                        >
                            <option value="">All Reps</option>
                            {users.map(u => {
                                const name = [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.login_name;
                                return <option key={u.id} value={u.id}>{name}</option>;
                            })}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>
                    <StateFilterSelect value={stateFilter} onChange={setStateFilter} options={stateCounts ?? undefined} />
                    {datePreset !== "all" && (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {(() => {
                                const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
                                if (!from_date) return null;
                                return from_date === to_date
                                    ? dayjs(from_date).format("DD MMM YYYY")
                                    : `${dayjs(from_date).format("DD MMM")} – ${dayjs(to_date).format("DD MMM YYYY")}`;
                            })()}
                        </span>
                    )}
                </div>

                {/* stage filter badges */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <button
                        onClick={() => setStageFilter(null)}
                        className={cn(
                            "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                            !stageFilter
                                ? "bg-gray-700 text-white border-gray-800"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
                        )}
                    >
                        All · {allData.length}
                    </button>

                    {stages.map(stage => (
                        <button
                            key={stage}
                            onClick={() => setStageFilter(stageFilter === stage ? null : stage)}
                            className={cn(
                                "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                                stageFilter === stage
                                    ? "bg-indigo-600 text-white border-indigo-700"
                                    : cn(
                                        STAGE_BADGE[stage] || "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
                                        "border-transparent hover:opacity-90"
                                    )
                            )}
                        >
                            {stage} · {stageCounts[stage] ?? 0}
                        </button>
                    ))}

                    {/* Search */}
                    <div className="ml-auto flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1 bg-white dark:bg-gray-800">
                        <Search className="h-3 w-3 text-gray-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Customer, mobile or rep…"
                            className="text-xs bg-transparent outline-none text-gray-700 dark:text-gray-300 w-36"
                        />
                    </div>
                </div>
            </div>

            {/* ── scrollable table + fixed pagination ── */}
            {loading ? (
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading timeline…</p>
                    </div>
                </div>
            ) : (
                <div className="flex-1 min-h-0 flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {/* scrollable table body */}
                    <div className="flex-1 min-h-0 overflow-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-8">#</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Date</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Customer</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Mobile No</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Rep</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Transition</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 max-w-[200px]">Remarks</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Next Follow-up</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-center">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {paginated.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="text-center py-10 text-xs text-gray-400 dark:text-gray-500">
                                            No records found
                                        </td>
                                    </tr>
                                ) : (
                                    paginated.map((row, idx) => (
                                        <tr
                                            key={`${row.customer_id}-${row.created_date}-${idx}`}
                                            className={cn("hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors",
                                                // Verified by the lead (email / WhatsApp link), as on the lead list.
                                                row.is_verified && "bg-emerald-50/70 dark:bg-emerald-900/15 shadow-[inset_3px_0_0_0_theme(colors.emerald.500)]")}
                                            onClick={() => navigate("/customers/customerdetails", {
                                                state: { formEdit: true, row: { id: row.customer_id } },
                                            })}
                                        >
                                            <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                <div>{dayjs(row.created_date).format("DD MMM YYYY")}</div>
                                                <div className="text-[10px] text-gray-400">{dayjs(row.created_date).format("hh:mm A")}</div>
                                            </td>
                                            <td className="px-3 py-2.5 font-medium text-gray-800 dark:text-gray-200 max-w-[160px]">
                                                <TruncatedText text={row.customer_name} />
                                                <ResearchMark research={row.research} className="mt-0.5 mr-1" />
                                                {row.is_verified && (
                                                    <span title="Email or mobile verified by the lead through an email / WhatsApp link"
                                                        className="mt-0.5 inline-flex items-center gap-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-1.5 py-px text-[9px] font-semibold text-emerald-700 dark:text-emerald-300">
                                                        <CheckCircle className="h-2.5 w-2.5" aria-hidden="true" />Verified
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                                {row.contact_number || "—"}
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 max-w-[100px] truncate">
                                                {row.user_name || "—"}
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <div className="flex items-center gap-1 flex-wrap">
                                                    {row.from_status && (
                                                        <span className={cn(
                                                            "px-2 py-0.5 rounded-full text-[10px] font-medium",
                                                            STAGE_BADGE[row.from_status] || "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                                                        )}>
                                                            {row.from_status}
                                                        </span>
                                                    )}
                                                    {row.from_status && row.to_status && (
                                                        <span className="text-gray-400">→</span>
                                                    )}
                                                    {row.to_status && (
                                                        <span className={cn(
                                                            "px-2 py-0.5 rounded-full text-[10px] font-medium",
                                                            STAGE_BADGE[row.to_status] || "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                                                        )}>
                                                            {row.to_status}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 max-w-[200px]">
                                                <TruncatedText text={row.remarks} lines={2} label="Remarks" />
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                {row.next_followup_date
                                                    ? <>
                                                        {dayjs(row.next_followup_date).format("DD MMM YYYY")}
                                                        <div className="text-[10px] text-gray-400">{dayjs(row.next_followup_date).format("hh:mm A")}</div>
                                                    </>
                                                    : <span className="text-gray-300 dark:text-gray-600">—</span>
                                                }
                                            </td>
                                            <td className="px-3 py-2.5 text-center">
                                                <span className={cn(
                                                    "px-2 py-0.5 rounded-full text-[10px] font-medium",
                                                    row.is_active
                                                        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                                        : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                                )}>
                                                    {row.is_active ? "Active" : "Inactive"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* fixed pagination footer */}
                    <div className="flex-shrink-0 px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 flex-wrap gap-2">
                        {/* rows per page */}
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">Rows per page</span>
                            <select
                                value={pageSize}
                                onChange={e => setPageSize(Number(e.target.value))}
                                className="text-[10px] border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none"
                            >
                                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {filtered.length === 0 ? '0' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, filtered.length)}`} of {filtered.length}
                            </span>
                        </div>

                        {/* page controls */}
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setPage(1)}
                                disabled={page === 1}
                                className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                            >«</button>
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                            ><ChevronLeft className="h-3 w-3" /></button>

                            {/* page number pills */}
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .reduce((acc, p, i, arr) => {
                                    if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((p, i) => p === '…'
                                    ? <span key={`ellipsis-${i}`} className="px-1 text-[10px] text-gray-400">…</span>
                                    : <button
                                        key={p}
                                        onClick={() => setPage(p)}
                                        className={cn(
                                            "min-w-[22px] px-1.5 py-0.5 text-[10px] rounded border transition-colors",
                                            p === page
                                                ? "bg-indigo-600 text-white border-indigo-600"
                                                : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        )}
                                    >{p}</button>
                                )
                            }

                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                            ><ChevronRight className="h-3 w-3" /></button>
                            <button
                                onClick={() => setPage(totalPages)}
                                disabled={page === totalPages}
                                className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                            >»</button>
                        </div>
                    </div>
                </div>
            )}
        </ContainerCard>
    );
};

export default TimelineReports;
